<script setup>
import { computed, onMounted, ref, watch } from "vue";
import { useStore } from "vuex";
import { useVuelidate } from "@vuelidate/core";
import { helpers, required } from "@vuelidate/validators";
import ModalBase from "@/components/modal/ModalBase.vue";
import AlertMessage from "@/components/AlertMessage.vue";
import ArgonAddressNormalize from "@/components/ArgonAddressNormalize.vue";
import ArgonLoadingButton from "@/components/ArgonLoadingButton.vue";
import ArgonSnackbar from "@/components/ArgonSnackbar.vue";
import { stopStore } from "@/_store/stopStore";

const props = defineProps({
  modalId: { type: String, required: true },
  stopId: {
    type: String,
    default() {
      return null;
    },
  },
});

const emit = defineEmits(["close", "success"]);
const modalStopNormalizeAddress = ref(null);
const vuexStore = useStore();
const bootstrap = vuexStore.state.bootstrap;
const stopsStore = stopStore();

const isLoading = ref(false);
const isSaving = ref(false);
const snackbar = ref(null);
const form = ref({
  address_normalized: {
    label: "",
    coordenates: null,
    normalized: false,
    postal_code: "",
  },
});
const rules = {
  address_normalized: {
    address: {
      required: helpers.withMessage("La dirección normalizada es requerida.", required),
    },
    normalized: {
      validNormalized: helpers.withMessage(
        "Debes validar la dirección antes de guardar.",
        (value, addressNormalized) => {
          const address = String(addressNormalized?.address || "").trim();
          if (!address) return true;
          return !!value;
        }
      ),
    },
  },
};
const v$ = useVuelidate(rules, form);
const addressErrorMessage = computed(() => v$.value?.address_normalized?.$errors?.[0]?.$message || "");
const stopDeclaredAddress = computed(() => stopsStore.stop?.address?.text || "");

function buildEmptyAddressNormalized() {
  return {
    label: "",
    coordenates: null,
    normalized: false,
    postal_code: "",
  };
}

function closeSnackbar() {
  snackbar.value = null;
}

function showError(message) {
  snackbar.value = {
    title: "Error",
    message: message || "No se pudo normalizar la dirección de la parada",
    type: "danger",
    color: "white",
  };
}

function showInfo(message) {
  snackbar.value = {
    title: "Error",
    message: message || "Valida los campos requeridos.",
    type: "danger",
    color: "white",
  };
}

async function validateForm() {
  const isValid = await v$.value.$validate();
  if (!isValid) showInfo(addressErrorMessage.value || "Valida los campos requeridos.");
  return isValid;
}

function loadStopDetail() {
  isLoading.value = true;
  closeSnackbar();
  v$.value.$reset();
  stopsStore.getStopById(props.stopId);
}

async function saveNormalizedAddress() {
  if (isSaving.value || isLoading.value) return;
  if (!(await validateForm())) return;

  isSaving.value = true;
  closeSnackbar();

  stopsStore.updateStopAddressNormalized(props.stopId, {
    address_normalized: form.value.address_normalized,
  });
}

function handleHide() {
  form.value.address_normalized = buildEmptyAddressNormalized();
  v$.value.$reset();
  stopsStore.stop = null;
  stopsStore.stopAddressNormalizedUpdated = null;
  stopsStore.error = null;
  emit("close");
}

watch(
  () => stopsStore.stop,
  (value) => {
    if (!isLoading.value || !value) return;
    form.value.address_normalized = value?.address_normalized || buildEmptyAddressNormalized();
    isLoading.value = false;
  }
);
watch(
  () => stopsStore.stopAddressNormalizedUpdated,
  (value) => {
    if (!isSaving.value || !value) return;
    isSaving.value = false;
    modalStopNormalizeAddress.value.closeModal();
    emit("success");
  }
);
watch(
  () => stopsStore.error,
  (value) => {
    if (!value) return;
    if (!isLoading.value && !isSaving.value) return;
    showError(value?.message || "Ha ocurrido un error.");
    isLoading.value = false;
    isSaving.value = false;
  }
);

onMounted(() => {
  const modal = new bootstrap.Modal(document.getElementById(props.modalId), {
    keyboard: true,
    backdrop: true,
  });
  modal.show();
  loadStopDetail();
});
</script>

<template>
  <modal-base
    :id="modalId"
    ref="modalStopNormalizeAddress"
    width="33rem"
    min-height="15rem"
    @hide="handleHide"
  >
    <template #header>
      <h6 class="mb-0">Normalizar dirección</h6>
    </template>

    <template #body>
      <alert-message
        title="Dirección declarada"
        type="info"
        class="mb-3"
      >
       {{ stopDeclaredAddress}}
      </alert-message>
      <div class="row align-items-center mb-0">
        <div class="col-12">
          <argon-address-normalize
            id="stop-normalize-address"
            v-model="form.address_normalized"
            :error="
              v$.address_normalized.address.$error ||
              v$.address_normalized.normalized.$error
            "
            :disabled="isLoading || isSaving"
            placeholder=""
            :label-max-length="140"
          />
        </div>
      </div>
    </template>

    <template #footer>
      <argon-loading-button
        class="btn bg-gradient-primary btn-xs"
        color="primary"
        variant="gradient"
        :label="isSaving ? 'Guardando...' : 'Guardar'"
        :loading="isSaving"
        :disable-button="isLoading || isSaving"
        @click="saveNormalizedAddress"
      />
    </template>

    <div class="position-fixed top-0 end-0 z-index-2 p-3">
      <argon-snackbar
        v-if="snackbar"
        :title="snackbar.title"
        :description="snackbar.message"
        :icon="{ component: 'ni ni-notification-70', color: snackbar.type }"
        :color="snackbar.color"
        :close-handler="closeSnackbar"
      />
    </div>
  </modal-base>
</template>
